/**
 * BuyNowButton：enable_me_free_gift 开启时加购并跳转购物车页；关闭时直接结账。
 *
 * Attributes:
 * @param {String} data-variant-id 产品变体 ID，多个变体用英文逗号分隔
 * @param {String} data-quantity 购买数量，可选，默认为1，多个变体的购买数量用英文逗号分隔
 * 
 * @example 
 * ```html
 * <script src="{{ 'component-buy-button.js' | asset_url }}" defer="defer"></script>
 * <buy-now-button data-variant-id="{{ product.first_available_variant.id }}" data-quantity="1" class="button round">
 *   Buy Now
 *   {%- render 'loading-spinner' -%}
 * </buy-now-button>
 * ```
 */
if (!customElements.get('buy-now-button')) {
  class BuyNowButton extends HTMLElement {
    _shopify_features_script = document.querySelector("script[id='shopify-features']");
    _shopify_features_json = JSON.parse(this._shopify_features_script.innerHTML);
    _checkout_headers = {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'authorization': 'Basic ' + btoa(this._shopify_features_json.accessToken)
    };

    get _variantId() { return this.getAttribute("data-variant-id") }
    get _quantity() { return this.getAttribute("data-quantity") ?? 1 }

    constructor() {
      super();
      this.loadingSpinner = this.querySelector('.loading__spinner ');
      this._processing = false;
      this._onClick = this.handleClick.bind(this);
    }

    isSaleClosed() {
      if (this.getAttribute('data-sale-gated') !== 'true') return false;
      const section = this.closest('[data-sale-open]');
      if (!section) return true;
      return section.getAttribute('data-sale-open') !== 'true';
    }

    handleClick() {
      if (this.isSaleClosed()) return;
      if (window.enableMeFreeGift) {
        this.handleMeFreeGiftAddToCart();
      } else {
        this.checkout();
      }
    }

    /**
     * ME 满赠开启：加购后跳转购物车页（与 PDP Buy Now 一致）
     */
    handleMeFreeGiftAddToCart() {
      if (this._processing) return;
      this._processing = true;
      this.startLoading();
      Promise.resolve(this.addItemsToCart())
        .then(() => {
          window.location.href = window.routes?.cart_url ?? '/cart';
        })
        .catch((err) => console.error(err))
        .finally(() => {
          this.finishLoading();
          this._processing = false;
        });
    }

    async addItemsToCart() {
      const variantIds = this._variantId.split(',');
      const quantities = this._quantity.split(',');
      const defaultQuantity = quantities[0] || 1;
      if (variantIds.length !== quantities.length) {
        console.warn('变体ID与数量的数量不匹配，将使用相同的数量值。');
        quantities.length = variantIds.length;
        quantities.fill(defaultQuantity);
      }
      const items = variantIds.map((id, index) => {
        return {
          form_type: 'product',
          utf8: '✓',
          id: parseInt(id, 10),
          quantity: parseInt(quantities[index], 10),
        };
      });
      const response = await fetch('/cart/add.json', {
        method: 'POST',
        headers: this._checkout_headers,
        body: JSON.stringify({ items: items }),
      });
      if (!response.ok) {
        if (response.status === 422) {
          alert('Inventory shortage!');
        }
        throw new Error('请求失败');
      }
    }

    connectedCallback() {
      this.addEventListener('click', this._onClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
    }
    /**
     * 直接结算
     */
    async checkout() {
      const variantIds = this._variantId.split(',');
      const quantities = this._quantity.split(",");
      if (window.cartGiftSettings) {
        if (this._processing) return;
        // 1. 先清空购物车
        await fetch('/cart/clear.js', { method: 'POST' });
        // 2. 加购主商品(card3可以一起购买配件)
        let data = JSON.stringify({ id: variantIds[0], quantity: 1 })
        if (variantIds.length > 1) {
          const items = variantIds.map((id, index) => {
            return {
              form_type: "product",
              utf8: "✓",
              id: parseInt(id, 10),
              quantity: parseInt(quantities[index], 10), // 使用对应的数量值
            };
          });
          let data_json = {
            items: items,
          };

          data = JSON.stringify(data_json);
        }
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: data
        });
        // 3. 获取最新cart并处理赠品
        const cartRes = await fetch('/cart.js');
        const cart = await cartRes.json();
        if (window.buyAGetBSecondCartGiftSettings && window.updateBuyAGetBSecondCartGift) {
          await window.updateBuyAGetBSecondCartGift(cart);
        }
        if (window.chooseStepGiftForCheckout) {
          await window.chooseStepGiftForCheckout(cart);
        }
        console.log(variantIds)
        // 4. 跳转到结账页（/checkout，自动带上赠品）
        window.location.href = '/checkout';
        this._processing = false;
        return;
      }


      const defaultQuantity = quantities[0] || 1; // 默认数量为1，如果 quantities 为空
      if (variantIds.length !== quantities.length) {
        console.warn("变体ID与数量的数量不匹配，将使用相同的数量值。");
        quantities.length = variantIds.length; // 将数量数组的长度调整为与变体ID数组相同
        quantities.fill(defaultQuantity); // 用默认数量填充整个数组
      }
      this.startLoading();
      // variantIds.push("10117311889722");
      // variantIds.push("9927944339770");
      // quantities.push("1");
      // quantities.push("1");
      this.checkoutByJson(variantIds, quantities)
        .catch((err) => {
          console.error(err);
          console.log(variantIds, quantities);
          this.checkoutByCartUrl(variantIds, quantities);
        })
        .finally(() => this.finishLoading());
    }

    checkoutByCartUrl(variantIds, quantities) {
      let parmaString = '';
      for (let i = 0; i < variantIds.length; i++) {
        const variantId = variantIds[i];
        const quantity = quantities[i];
        if (i == 0) {
          parmaString = `${parmaString}${variantId}:${quantity}`;
        } else {
          parmaString = `${parmaString},${variantId}:${quantity}`;
        }
      }
      const cartRoute = window.routes?.cart_url ?? '/cart';;
      const checkoutUrl = `${cartRoute}/${parmaString}`;
      console.log(checkoutUrl);
      window.location.href = checkoutUrl;
    }

    async checkoutByJson(variantIds, quantities) {
      let lineItems = variantIds.map((id, index) => {
        return {
          "variant_id": parseInt(id, 10),
          "quantity": parseInt(quantities[index], 10),
        }
      });

      let checkout_json = {
        "checkout": {
          "line_items": lineItems,
          "secret": true,
          "wallet_name": "Checkout",
          "is_upstream_button": false,
          "page_type": "product",
          "has_selling_plans": false,
          "presentment_currency": window.shop_currency,
          "country": window.shop_locale_upcase,
        }
      }
      let data = JSON.stringify(checkout_json);
      return fetch("/wallets/checkouts.json",
        {
          method: 'POST',
          headers: this._checkout_headers,
          body: data,
        }
      ).then(function (response) {
        if (response.ok) {
          return response.text();
        }
      }).then(function (strj) {
        let checkout_json = JSON.parse(strj);
        location.href = checkout_json.checkout.web_url;
      });
    }

    startLoading() {
      this.setAttribute("aria-disabled", true);
      this.classList.add("loading");
      this.loadingSpinner?.classList.remove("hidden");
    }

    finishLoading() {
      this.removeAttribute("aria-disabled");
      this.classList.remove("loading");
      this.loadingSpinner?.classList.add("hidden");
    }
  }

  customElements.define('buy-now-button', BuyNowButton);
}
